import {
  get,
  isEmpty,
  uniq,
  values,
  filter,
  uniqBy,
  keyBy,
} from 'lodash'
import { createSelector } from 'reselect'
import { getItems } from 'store/Items/selector'
import { getMenus } from 'store/Menus/selectors'
import { find } from 'lodash'

// Service Types //

export const getServiceTypes = (state) =>
  state?.revenueCenters?.serviceTypes?.byId ?? {}

export const getNewCurrentServiceType = (state, uuid) => {
  const allNewServiceTypes = getServiceTypes(state)
  const newCurrentServiceType = find(
    allNewServiceTypes,
    (serviceType) => serviceType.uuid === uuid
  )
  return newCurrentServiceType ?? {}
}

export const getIsServiceTypeLoading = (state) =>
  state?.revenueCenters?.serviceTypes?.isLoading

// Service Types with items //

export const getServiceTypesWithItems = (state) =>
  state?.revenueCenters?.serviceTypes?.serviceTypesWithItems ?? {}

// Location Settings //

export const getLocationSettings = (state) =>
  state?.revenueCenters?.locationSettings?.locationSettingsData ?? {}

// Menu Settings //

export const getAllMenuSettings = (state) =>
  state?.revenueCenters?.menuSettings?.menuSettingsData ?? {}

export const getMenuSettings = (state, id) =>
  state?.revenueCenters?.menuSettings?.menuSettingsData[id] ?? {}

// Revenue Centers //

export const getRevenueCenters = (state) =>
  values(get(state, 'revenueCenters.byId'))

export const getRevenueCenter = (state, revenueCenterId) =>
  get(state, `revenueCenters.byId.${revenueCenterId}`, {})

export const makeGetRevenueCenter = () =>
  createSelector([getRevenueCenter], (revenueCenter) => revenueCenter)

export const getIsRevenueCentersLoading = (state) =>
  state?.revenueCenters?.isLoading ?? false

export const getSearchGlobalRevenueCenters = (state) =>
  state?.revenueCenters?.filters?.searchGlobalRevenueCetners ?? ''

export const getServiceTypesForRevenueCenter = (state, id) => {
  const allMenus = getMenus(state)

  const menusAssignedToRevenueCenter = allMenus.filter(
    (menu) => menu.standId === id
  )

  const serviceTypes =
    menusAssignedToRevenueCenter.map((menu) => menu.serviceType) ?? []

  const uniqServiceTypes = uniq(serviceTypes)
  return uniqServiceTypes
}

export const getMenusForRevenueCenterWithCurrentCreatedMenus = (
  state,
  id
) => {
  const allMenus = getMenus(state)

  const menusInStore = getServiceTypes(state)

  const menusToFilter = isEmpty(menusInStore)
    ? allMenus
    : menusInStore

  const menusAssignedToRevenueCenter = filter(
    menusToFilter,
    (menu) => menu.standId === id
  )

  return menusAssignedToRevenueCenter ?? []
}

export const getMenusForRevenueCenter = (state, id) => {
  //There is require revenue center id not uuid!
  const allMenus = getMenus(state)

  const menusAssignedToRevenueCenter = filter(
    allMenus,
    (menu) => menu.standId === id
  )

  return menusAssignedToRevenueCenter ?? []
}

//this selector is created to find existing current menu, not currently created
export const getCurrentMenuForRevenueCenterFromExistingMenus = (
  state,
  revenueCenterId,
  menuId
) => {
  const menusForRevenueCenter = getMenusForRevenueCenter(
    state,
    revenueCenterId
  )
  const currentMenu = menusForRevenueCenter.find(
    (menuForRevenueCenter) => menuForRevenueCenter.uuid === menuId
  )
  return currentMenu ?? {}
}

export const getFilteredByServiceType = (state) =>
  state?.revenueCenters?.filters?.byServiceTypes ?? []

export const getFilteredRevenueCenters = createSelector(
  getFilteredByServiceType,
  getRevenueCenters,
  getMenus,
  (filteredByServiceType, revenueCenters, allMenus) => {
    if (isEmpty(filteredByServiceType)) return revenueCenters

    const filteredMenus = allMenus.filter((menu) => {
      const serviceTypeName =
        menu.serviceType === 'Concessions'
          ? 'Pickup'
          : menu.serviceType

      return serviceTypeName.includes(filteredByServiceType)
    })

    const filteredMenusStandId = filteredMenus.map(
      (menu) => menu.standId
    )

    const filteredRevenueCentersByFilteredMenusStandId =
      revenueCenters.filter((revenueCenter) =>
        filteredMenusStandId.includes(revenueCenter.id)
      )
    return filteredRevenueCentersByFilteredMenusStandId
  }
)

export const getFilteredDataForRevenueCenter = (state, id) => {
  if (isEmpty(state.revenueCenters.byId)) {
    return {
      location: [],
      cuisine: [],
    }
  }

  return (
    state?.revenueCenters?.byId[id]?.filterData ?? {
      location: [],
      cuisine: [],
    }
  )
}

export const getEditedFieldValuesRevenueCenter = (state) =>
  state?.revenueCenters?.editedFormFields ?? {}

// assigned items
export const getAssignedItemsToMenu = (state, uuid) => {
  const allItems = getItems(state)
  const assignedItems = allItems.filter((item) =>
    item?.menuUuids?.includes(uuid)
  )
  return assignedItems ?? []
}

// Availability

export const getIsLoadingAvailability = (state) =>
  state?.revenueCenters?.availability?.loading

export const getMenusStatus = (state, id) => {
  const revenueCenterAvailability =
    state?.revenueCenters?.availability?.byId[id]

  const uniqServiceType = uniqBy(
    revenueCenterAvailability?.serviceTypes,
    'type'
  )

  const serviceTypesAvailability = uniqServiceType.map(
    ({ type, availability }) => {
      const upperCaseType = type?.toUpperCase()
      return {
        type: upperCaseType,
        availability,
      }
    }
  )

  return serviceTypesAvailability
}
